import { useMemo, useState } from 'react';
import { BaseButton } from '@repo/common';

import { ReactComponent as BackpackOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_backpack_outline.svg';
import { ReactComponent as BusinessBriefcaseOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_business-briefcase_version1_outline.svg';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';
import { ReactComponent as GraduateOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_graduate_outline.svg';
import { ReactComponent as LanguageProgramOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_language-program_outline.svg';
import { ReactComponent as ManualBookOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_book_outline.svg';
import { ReactComponent as ReloadIcon } from '@repo/common/assets/icons/24px/icon_24px_reload.svg';
import {
  searchMajors,
  searchTools,
  searchUniversities,
} from '../../../api/pathfinder/pathfinder.api';
import PathfinderSchoolMasterSearchSelect from '../search/PathfinderSchoolMasterSearchSelect';
import PathfinderMobileDropdown from './PathfinderMobileDropdown';
import PathfinderMobileSearchSelect from './PathfinderMobileSearchSelect';
import PathfinderMobileSelect from './PathfinderMobileSelect';

const ROLE_GRID_QUESTION_CODES = new Set([
  'Q_JOB_ROLE',
  'Q_JOB_INDUSTRY',
  'Q_ROLE_EXPLORATION',
]);
const NO_ICON_QUESTION_CODES = new Set([
  'Q_PORTFOLIO_LANG',
  'Q_KPI_EVIDENCE',
  'Q_JD_INTERVIEW',
  'Q_NATIONALITY_CHECK',
  'Q_ROLE_TASKS',
  'Q_ROLE_SUBFUNCTION',
]);
const TALL_CARD_QUESTION_CODES = new Set([
  'Q_KR_DEGREE',
  'Q_PORTFOLIO_AWARD',
  'Q_OPTIONAL_CAREER_LINK',
]);
const CARD_QUESTION_CODES = new Set([
  'Q_KR_DEGREE',
  'Q_FIRST_D10',
  'Q_PORTFOLIO_AWARD',
  'Q_OPTIONAL_CAREER_LINK',
]);
const LEFT_ALIGN_QUESTION_CODES = new Set(['Q_KPI_EVIDENCE']);
const NO_LEVEL_CERT_CODES = new Set(['LANG_NONE', 'LANG_TOPIK5']);
const NO_APPEND_CERT_CODES = new Set(['LANG_NONE']);
const EMPTY_LANG_ROW = { certType: '', level: '' };
const DEFAULT_MAJOR_FIELD_OPTIONS = [
  'Business Administration',
  'Computer Science',
  'Economics',
  'Mechanical Engineering',
  'Marketing',
  'Psychology',
  'Visual Design',
].map((label) => ({
  optionCode: `FIELD_${label.toUpperCase().replace(/[^A-Z0-9]+/g, '_')}`,
  optionLabel: label,
}));

const GOAL_TYPE_OPTION_ICONS = {
  GOAL_JOB: <BusinessBriefcaseOutlineIcon />,
  GOAL_GRAD: <GraduateOutlineIcon />,
  GOAL_UG: <ManualBookOutlineIcon />,
  GOAL_TRANSFER: <ReloadIcon />,
  GOAL_LANGUAGE: <LanguageProgramOutlineIcon />,
};

const UNIVERSITY_CONTEXT_OPTION_ICONS = {
  CTX_UNI_UNIVERSITY: <GraduateOutlineIcon />,
  CTX_UNI_RESEARCH: <ManualBookOutlineIcon />,
};

export const parsePathfinderQuestionConfig = (configJson) => {
  if (!configJson) return {};
  try {
    return typeof configJson === 'string' ? JSON.parse(configJson) : configJson;
  } catch {
    return {};
  }
};

export const filterPathfinderOptionsByParent = (options, parentAnswer, dependsOnMap) => {
  if (!parentAnswer) return options;

  if (
    dependsOnMap &&
    typeof dependsOnMap === 'object' &&
    dependsOnMap !== true
  ) {
    const allowed = dependsOnMap[parentAnswer];
    if (!allowed) return [];
    const allowedSet = new Set(allowed);
    return options.filter((option) => allowedSet.has(option.optionCode));
  }

  const parentCodes = Array.isArray(parentAnswer) ? parentAnswer : [parentAnswer];
  return options.filter((option) => {
    if (!option.optionValue) return true;
    const allowedParents = option.optionValue.split(',').map((value) => value.trim());
    return parentCodes.some((code) => allowedParents.includes(code));
  });
};

const toSelectOption = (option) => ({
  id: option.optionCode,
  title: option.optionLabel,
  caption: option.description ?? '',
  value: option.optionCode,
  label: option.optionLabel,
});

const toMobileSelectOption = (questionCode, option, answers = {}) => {
  if (
    questionCode === 'Q_LANG_USE_CONTEXT' &&
    answers.Q_GOAL_TYPE === 'GOAL_LANGUAGE' &&
    answers.Q_LANG_PURPOSE === 'LANG_CAREER'
  ) {
    const careerContextLabel =
      option.optionCode === 'CTX_CAREER_CLIENT'
        ? 'Client & external communication'
        : option.optionLabel;
    return {
      id: option.optionCode,
      title: careerContextLabel,
      caption: '',
      value: option.optionCode,
      label: careerContextLabel,
    };
  }

  if (
    questionCode === 'Q_JOB_ROLE' &&
    answers.Q_GOAL_TYPE === 'GOAL_LANGUAGE' &&
    answers.Q_LANG_PURPOSE === 'LANG_CAREER'
  ) {
    const roleLabel = option.optionCode === 'ROLE_etc' ? 'etc' : option.optionLabel;
    return {
      id: option.optionCode,
      title: roleLabel,
      caption: '',
      value: option.optionCode,
      label: roleLabel,
    };
  }

  if (questionCode !== 'Q_GOAL_DETAIL') return toSelectOption(option);

  const visaLabel =
    option.optionCode === 'NO_VISA'
      ? 'None yet'
      : [option.optionLabel, option.description].filter(Boolean).join(' ');

  return {
    id: option.optionCode,
    title: visaLabel,
    caption: '',
    value: option.optionCode,
    label: visaLabel,
  };
};

const getTallCardIcon = (questionCode) =>
  questionCode === 'Q_OPTIONAL_CAREER_LINK'
    ? <BackpackOutlineIcon />
    : <GraduateOutlineIcon />;

const getSelectIcon = (questionCode, optionCode) => {
  if (questionCode === 'Q_GOAL_TYPE') return GOAL_TYPE_OPTION_ICONS[optionCode];
  if (questionCode === 'Q_LANG_USE_CONTEXT') {
    return UNIVERSITY_CONTEXT_OPTION_ICONS[optionCode];
  }
  if (TALL_CARD_QUESTION_CODES.has(questionCode)) return getTallCardIcon(questionCode);
  return undefined;
};

const toDropdownOption = (option) => ({
  value: option.optionCode,
  label: option.optionLabel,
});

const normalizeSearchOptions = (options) =>
  options.map((option) => ({
    value: option.optionCode,
    label: option.optionLabel,
  }));

const getSearchPlaceholder = (question, config) => {
  if (
    question.questionCode === 'Q_CURRENT_MAJOR_FIELD' ||
    question.questionCode === 'Q_TARGET_MAJOR_FIELD' ||
    question.questionCode === 'Q_MAJOR_LINK'
  ) {
    return 'Your major’s name in english';
  }
  if (config.searchType === 'KR_UNIVERSITY') return 'e.g. Yonsei University';
  if (config.searchType === 'TOOL') return 'Search tools';
  return question.helperText || 'Search';
};

const getMobileSearchPlaceholder = (question, config, answers = {}) => {
  if (
    question.questionCode === 'Q_TARGET_MAJOR_FIELD' &&
    answers.Q_GOAL_TYPE !== 'GOAL_JOB'
  ) {
    return 'e.g. Business, Computer Science';
  }
  if (
    question.questionCode === 'Q_CURRENT_MAJOR_FIELD' ||
    question.questionCode === 'Q_TARGET_MAJOR_FIELD' ||
    question.questionCode === 'Q_MAJOR_LINK'
  ) {
    return "Your major's name in english";
  }
  if (config.searchType === 'KR_UNIVERSITY') return 'e.g. Yonsei University';
  if (config.searchType === 'TOOL') return 'Search tools';
  return question.helperText || 'Search';
};

const getSearchLoader = (question, config) => {
  if (
    question.questionCode === 'Q_CURRENT_MAJOR_FIELD' ||
    question.questionCode === 'Q_TARGET_MAJOR_FIELD' ||
    question.questionCode === 'Q_MAJOR_LINK'
  ) {
    return async (keyword) => {
      const majors = await searchMajors(keyword);
      return majors.map((major) => ({
        value: String(major.id),
        label: major.nameEn,
      }));
    };
  }

  if (config.searchType === 'KR_UNIVERSITY') {
    return async (keyword) => {
      const universities = await searchUniversities(keyword);
      return universities.map((university) => ({
        value: university.schoolCode || university.schoolNameEn,
        label: university.schoolNameEn || university.schoolNameKo,
      }));
    };
  }

  if (config.searchType === 'TOOL') {
    return async (keyword) => {
      const tools = await searchTools(keyword);
      return tools.map((tool) => ({
        value: tool.name,
        label: tool.name,
      }));
    };
  }

  return null;
};

const isLangRowComplete = (row) => {
  if (!row?.certType) return false;
  return NO_LEVEL_CERT_CODES.has(row.certType) || Boolean(row.level);
};

const shouldAppendLangRow = (row) =>
  isLangRowComplete(row) && !NO_APPEND_CERT_CODES.has(row?.certType);

const ensureEditableLangRow = (rows) =>
  rows.some((row) => !isLangRowComplete(row)) ? rows : [...rows, { ...EMPTY_LANG_ROW }];

function PathfinderMobileLangScorePicker({ options = [], value, onChange }) {
  const [rows, setRows] = useState(() => {
    if (!Array.isArray(value) || value.length === 0) return [{ ...EMPTY_LANG_ROW }];
    const last = value[value.length - 1];
    return shouldAppendLangRow(last) ? [...value, { ...EMPTY_LANG_ROW }] : [...value];
  });

  const { certOptions, levelOptionsMap } = useMemo(() => {
    const cert = [];
    const levelMap = {};

    options.forEach((option) => {
      const item = toDropdownOption(option);
      if (option.description == null || option.description === '') {
        cert.push(item);
      } else {
        if (!levelMap[option.description]) levelMap[option.description] = [];
        levelMap[option.description].push(item);
      }
    });

    return { certOptions: cert, levelOptionsMap: levelMap };
  }, [options]);

  const optionLabelMap = useMemo(() => {
    const entries = options.map((option) => [option.optionCode, option.optionLabel]);
    return new Map(entries);
  }, [options]);

  const completeRows = rows.filter(isLangRowComplete);
  const editableRows = rows.filter((row) => !isLangRowComplete(row));

  const emitRows = (nextRows) => {
    const normalizedRows = ensureEditableLangRow(nextRows);
    setRows(normalizedRows);
    onChange?.(normalizedRows.filter(isLangRowComplete));
  };

  const updateRow = (index, patch) => {
    const nextRows = rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, ...patch } : row
    );
    const updatedRow = nextRows[index];
    const isLast = index === nextRows.length - 1;

    if (isLast && shouldAppendLangRow(updatedRow)) {
      nextRows.push({ ...EMPTY_LANG_ROW });
    }

    emitRows(nextRows);
  };

  const removeCompleteRow = (targetIndex) => {
    let completeIndex = -1;
    const nextRows = rows.filter((row) => {
      if (!isLangRowComplete(row)) return true;
      completeIndex += 1;
      return completeIndex !== targetIndex;
    });

    emitRows(nextRows);
  };

  return (
    <div className='flex flex-col w-full gap-4'>
      {completeRows.length > 0 ? (
        <div className='flex flex-wrap items-start w-full min-h-6 gap-x-4 gap-y-1'>
          {completeRows.map((row, index) => {
            const certLabel = optionLabelMap.get(row.certType) ?? row.certType;
            const levelLabel = row.level ? optionLabelMap.get(row.level) ?? row.level : '';
            const summary = levelLabel ? `${certLabel}, Level: ${levelLabel}` : certLabel;

            return (
              <BaseButton
                key={`${row.certType}-${row.level || 'none'}-${index}`}
                type='text'
                size='m'
                htmlType='button'
                typo={false}
                isStatic
                gap='gap-[2px]'
                iconPosition='after'
                icon={
                  <span className='flex items-center justify-center size-6 shrink-0'>
                    <CancelIcon
                      className='size-4 text-text-primary'
                      aria-hidden
                    />
                  </span>
                }
                onClick={() => removeCompleteRow(index)}
                textWrapperClass='h-auto min-w-0 flex-none truncate typo-b14 text-text-primary'
                className='h-auto max-w-full min-w-0 cursor-pointer items-center rounded-none border-0 bg-transparent p-0 text-left'
              >
                {summary}
              </BaseButton>
            );
          })}
        </div>
      ) : null}

      {editableRows.map((row) => {
        const index = rows.indexOf(row);
        const levelOptions = levelOptionsMap[row.certType] ?? [];
        const isLevelDisabled = !row.certType || NO_LEVEL_CERT_CODES.has(row.certType);

        return (
          <div key={`${index}-${row.certType || 'empty'}`} className='flex flex-col gap-4'>
            <PathfinderMobileDropdown
              value={row.certType}
              onChange={(certType) => updateRow(index, { certType, level: '' })}
              options={certOptions}
              placeholder='Certification Type'
            />
            <div className={isLevelDisabled ? 'pointer-events-none opacity-40' : ''}>
              <PathfinderMobileDropdown
                value={row.level}
                onChange={(level) => updateRow(index, { level })}
                options={levelOptions}
                placeholder='Level'
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PathfinderMobileMultiSelect({
  question,
  options,
  value,
  onChange,
  exclusiveOptionCode,
  hideIcons = false,
}) {
  const selectedValues = Array.isArray(value) ? value : [];
  const selectedSet = new Set(selectedValues);
  const isRoleGrid = ROLE_GRID_QUESTION_CODES.has(question.questionCode);
  const hasNoIcon = hideIcons || NO_ICON_QUESTION_CODES.has(question.questionCode);
  const textAlign = LEFT_ALIGN_QUESTION_CODES.has(question.questionCode) ? 'left' : 'auto';

  const toggleOption = (optionCode) => {
    const isSelected = selectedSet.has(optionCode);

    if (exclusiveOptionCode && optionCode === exclusiveOptionCode) {
      onChange(isSelected ? [] : [optionCode]);
      return;
    }

    const withoutExclusive = exclusiveOptionCode
      ? selectedValues.filter((selected) => selected !== exclusiveOptionCode)
      : selectedValues;
    const nextValues = isSelected
      ? withoutExclusive.filter((selected) => selected !== optionCode)
      : [...withoutExclusive, optionCode];
    onChange(nextValues);
  };

  return (
    <div className={isRoleGrid ? 'flex flex-wrap gap-2' : 'flex flex-col gap-3'}>
      {options.map((option) => (
        <PathfinderMobileSelect
          key={option.optionCode}
          size={isRoleGrid ? 'chip' : 'small'}
          icon={hasNoIcon ? null : undefined}
          iconPosition={hasNoIcon ? 'none' : 'left'}
          textAlign={textAlign}
          title={option.optionLabel}
          caption={option.description ?? ''}
          state={selectedSet.has(option.optionCode) ? 'selected' : 'default'}
          onClick={() => toggleOption(option.optionCode)}
        />
      ))}
    </div>
  );
}

function PathfinderMobileExpBlockInput({ question, value, onChange }) {
  const config = parsePathfinderQuestionConfig(question.configJson);
  const {
    step1Title = 'What is your highest level of education?',
    step1Helper = 'Select the option that best describes your background',
    step2Title = 'Which types of experience related to your target role do you have?',
    step2Helper = 'Select all that apply',
    step1Options: step1Codes = [],
    step2Options: step2Codes = [],
    step2ExclusiveOption = 'EXP_NONE_TYPE',
  } = config;
  const currentValue = value || {};
  const selectedStep2 = Array.isArray(currentValue.step2) ? currentValue.step2 : [];
  const isTimelineMode = step1Codes.length > 0 && step2Codes.length === 0;

  const step1Options = question.options.filter((option) =>
    step1Codes.includes(option.optionCode)
  );
  const step2Options = question.options.filter((option) =>
    step2Codes.includes(option.optionCode)
  );

  const emit = (patch) => {
    const nextValue = {
      step1: currentValue.step1 ?? null,
      step2: selectedStep2,
      step2Durations: currentValue.step2Durations ?? {},
      experiences: currentValue.experiences ?? [],
      ...patch,
    };
    const isComplete = Boolean(nextValue.step1) && nextValue.step2.length > 0;
    onChange?.({
      ...nextValue,
      complete: isComplete,
    });
  };

  if (isTimelineMode) {
    const selectedStep1 = Array.isArray(currentValue.step1) ? currentValue.step1 : [];

    return (
      <div className='flex flex-col gap-3'>
        <div className='flex flex-col gap-1'>
          <p className='typo-b16Strong text-text-tertiary'>{step1Title}</p>
          <p className='typo-b14 text-text-placeholder'>{step1Helper}</p>
        </div>
        <PathfinderMobileMultiSelect
          question={question}
          options={step1Options}
          value={selectedStep1}
          onChange={(nextStep1) =>
            onChange?.({
              step1: nextStep1,
              step2: nextStep1,
              step2Durations: {},
              experiences: [],
              complete: nextStep1.length > 0,
            })
          }
        />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-3'>
        <div className='flex flex-col gap-1'>
          <p className='typo-b16Strong text-text-tertiary'>{step1Title}</p>
          <p className='typo-b14 text-text-placeholder'>{step1Helper}</p>
        </div>
        <div className='flex flex-col gap-3'>
          {step1Options.map((option) => (
            <PathfinderMobileSelect
              key={option.optionCode}
              icon={null}
              iconPosition='none'
              textAlign='left'
              title={option.optionLabel}
              caption={option.description ?? ''}
              state={currentValue.step1 === option.optionCode ? 'selected' : 'default'}
              onClick={() => emit({ step1: option.optionCode })}
            />
          ))}
        </div>
      </div>

      {currentValue.step1 ? (
        <div className='flex flex-col gap-3'>
          <div className='flex flex-col gap-1'>
            <p className='typo-b16Strong text-text-tertiary'>{step2Title}</p>
            <p className='typo-b14 text-text-placeholder'>{step2Helper}</p>
          </div>
          <PathfinderMobileMultiSelect
            question={{ ...question, questionCode: 'Q_EXP_BLOCK_STEP2' }}
            options={step2Options}
            value={selectedStep2}
            onChange={(nextStep2) => emit({ step2: nextStep2 })}
            exclusiveOptionCode={step2ExclusiveOption}
          />
        </div>
      ) : null}
    </div>
  );
}

function PathfinderMobileQuestionInput({
  question,
  value,
  onChange,
  answers = {},
  answerLabelMap,
}) {
  const { answerType, options = [] } = question;
  const config = parsePathfinderQuestionConfig(question.configJson);

  const filteredOptions =
    config.dependsOn && config.dependsOnMap
      ? filterPathfinderOptionsByParent(
          options,
          answers[config.dependsOn],
          config.dependsOnMap
        )
      : options;

  switch (answerType) {
    case 'LANG_SCORE_PICKER':
      return (
        <PathfinderMobileLangScorePicker
          options={filteredOptions}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
        />
      );

    case 'DROPDOWN_SINGLE':
      return (
        <PathfinderMobileDropdown
          options={filteredOptions.map(toDropdownOption)}
          value={value || ''}
          onChange={onChange}
          placeholder='Please select'
          menuClassName='max-h-[228px] overflow-y-auto'
        />
      );

    case 'SUMMARY_SEARCH':
      return (
        <PathfinderSchoolMasterSearchSelect
          value={value || null}
          onChange={onChange}
          selectedLabelValue={value ? answerLabelMap?.[value] : ''}
          placeholder={question.helperText || 'Search your school'}
        />
      );

    case 'SEARCH_SELECT': {
      const isSingleValue = config.maxSelections === 1 || question.questionCode === 'Q_CURRENT_MAJOR_FIELD' || question.questionCode === 'Q_TARGET_MAJOR_FIELD';
      const showTargetMajorFieldSuggestions =
        question.questionCode === 'Q_TARGET_MAJOR_FIELD' &&
        answers.Q_GOAL_TYPE !== 'GOAL_JOB';
      const normalizedOptionsSource =
        showTargetMajorFieldSuggestions
          ? DEFAULT_MAJOR_FIELD_OPTIONS
          : filteredOptions;
      const selectedValues = isSingleValue
        ? value
          ? [value]
          : []
        : Array.isArray(value)
        ? value
        : [];

      return (
	          <PathfinderMobileSearchSelect
	            values={selectedValues}
            onChange={(nextValues, selectedOptions) =>
              onChange(isSingleValue ? nextValues[0] ?? null : nextValues, {
                selectedOptions,
              })
            }
            options={normalizeSearchOptions(normalizedOptionsSource)}
            selectedOptionLabels={answerLabelMap}
            loadOptions={getSearchLoader(question, config)}
	          maxSelections={
	            isSingleValue
	              ? 1
	              : config.maxSelections ?? (config.searchType === 'KR_UNIVERSITY' ? 3 : Infinity)
	          }
	          placeholder={getMobileSearchPlaceholder(question, config, answers)}
	          showDefaultSuggestions={showTargetMajorFieldSuggestions}
	          defaultSuggestionTitle='Most popular fields :'
	          limitMessage={(count) => `You can select up to ${count} options.`}
	        />
      );
    }

    case 'EXP_BLOCK_FLOW':
      return (
        <PathfinderMobileExpBlockInput
          question={question}
          value={value}
          onChange={onChange}
        />
      );

    case 'DROPDOWN_MULTI':
    case 'MULTI_SELECT_OPTION':
    case 'MULTI_SELECT':
      return (
	        <PathfinderMobileMultiSelect
	          question={question}
	          options={filteredOptions}
	          value={value}
	          onChange={onChange}
	          exclusiveOptionCode={config.exclusiveOption}
	          hideIcons={answers.Q_GOAL_TYPE === 'GOAL_LANGUAGE'}
	        />
      );

    case 'SINGLE_SELECT_CARD':
    case 'SINGLE_SELECT_OPTION':
    case 'SINGLE_SELECT':
    default: {
      const isRoleGrid =
        ROLE_GRID_QUESTION_CODES.has(question.questionCode) ||
        config.layoutVariant === 'role-grid' ||
        config.layoutVariant === 'visa-grid';
      const isCard = CARD_QUESTION_CODES.has(question.questionCode) || answerType === 'SINGLE_SELECT_CARD';
	      const hasNoIcon = NO_ICON_QUESTION_CODES.has(question.questionCode);
		      const showUniversityContextIcons =
		        answers.Q_GOAL_TYPE === 'GOAL_LANGUAGE' &&
		        answers.Q_LANG_PURPOSE === 'LANG_UNIVERSITY' &&
		        question.questionCode === 'Q_LANG_USE_CONTEXT';
		      const hideLanguageIcons =
		        answers.Q_GOAL_TYPE === 'GOAL_LANGUAGE' &&
		        question.questionCode !== 'Q_GOAL_TYPE' &&
		        !showUniversityContextIcons;
	      const selectOptions = filteredOptions.map((option) =>
	        toMobileSelectOption(question.questionCode, option, answers)
	      );

      return (
        <div
          className={
            isRoleGrid
              ? 'flex flex-wrap gap-2'
              : isCard && selectOptions.length === 2
              ? 'grid grid-cols-2 gap-2'
              : 'flex flex-col gap-3'
          }
        >
          {selectOptions.map((option) => (
            <PathfinderMobileSelect
              key={option.id}
              size={
                isRoleGrid
                  ? 'chip'
                  : TALL_CARD_QUESTION_CODES.has(question.questionCode)
                  ? 'tall'
                  : isCard
                  ? 'large'
                  : 'small'
              }
	              icon={
	                hasNoIcon || hideLanguageIcons
	                  ? null
		                  : getSelectIcon(question.questionCode, option.id)
	              }
	              iconPosition={hasNoIcon || hideLanguageIcons ? 'none' : isCard ? 'center' : 'left'}
              textAlign={LEFT_ALIGN_QUESTION_CODES.has(question.questionCode) ? 'left' : 'auto'}
              title={option.title}
              caption={option.caption}
              state={value === option.id ? 'selected' : 'default'}
              onClick={() => onChange(option.id)}
            />
          ))}
        </div>
      );
    }
  }
}

export default PathfinderMobileQuestionInput;
